<template>
  <SCAInputWrapper
    :label="label"
    :label-position="labelPosition"
    :appearance="appearance"
    :helper-text="helperText"
    :optional-label-text="optionalLabelText"
    :validation-error="validationError"
    :required="required"
    :html-for="htmlFor"
    :tooltip="tooltip"
    :help-link="helpLink"
  >
    <SCASelectBase
      v-model="modelProxy"
      v-model:search-term="searchTermProxy"
      :items="items"
      :search="search"
      :disabled="disabled"
      :placeholder="placeholder"
      :icon="icon"
      :alert="alert"
      :appearance="appearance"
      :hide-popover-on-value-change="hidePopoverOnValueChange"
      :has-more="hasMore"
      :loading-more="loadingMore"
      :html-for="htmlFor"
      :disable-hover-color-tag="disableHoverColorTag"
      :use-smaller-vertical-padding="useSmallerVerticalPadding"
      :item-icon-size="itemIconSize"
      :no-mobile-margin="noMobileMargin"
      :truncate-closed-tag="truncateClosedTag"
      @opened="emit('opened')"
      @closed="emit('closed')"
      @load-more="emit('loadMore')"
    >
      <template v-if="$slots.triggerContent" #triggerContent>
        <slot name="triggerContent" :item="selectedItem" />
      </template>
      <template v-if="$slots.input" #input>
        <slot name="input" />
      </template>
      <template v-if="$slots.after" #after>
        <slot name="after" />
      </template>
      <template v-if="$slots.tag" #tag="{ item }">
        <slot name="tag" :item="item" />
      </template>
    </SCASelectBase>
  </SCAInputWrapper>
</template>

<script setup lang="ts">
import { useVModel } from "@vueuse/core";
import type { Ref } from "vue";
import { computed } from "vue";

import type { IconName } from "./icons/iconNames.generated";
import SCAInputWrapper from "./SCAInputWrapper.vue";
import SCASelectBase from "./SCASelectBase.vue";
import type { InputAppearance, LabelPosition, SelectableItem } from "./types";

const props = withDefaults(
  defineProps<{
    modelValue?: SelectableItem["id"];
    label?: string;
    optionalLabelText?: string;
    helperText?: string | Ref<string>;
    validationError?: boolean;
    items: SelectableItem[];
    search?: "client" | "custom";
    searchTerm?: string;
    disabled?: boolean;
    placeholder?: string;
    icon?: IconName;
    htmlFor?: string;
    alert?: boolean;
    hidePopoverOnValueChange?: boolean;
    required?: boolean;
    labelPosition?: LabelPosition;
    appearance?: InputAppearance;
    hasMore?: boolean;
    loadingMore?: boolean;
    tooltip?: string;
    helpLink?: string;
    disableHoverColorTag?: boolean;
    useSmallerVerticalPadding?: boolean;
    itemIconSize?: string;
    noMobileMargin?: boolean;
    truncateClosedTag?: boolean;
  }>(),
  {
    label: undefined,
    optionalLabelText: undefined,
    modelValue: undefined,
    helperText: undefined,
    validationError: false,
    disabled: false,
    search: undefined,
    searchTerm: "",
    placeholder: undefined,
    icon: undefined,
    htmlFor: undefined,
    alert: false,
    hidePopoverOnValueChange: true,
    required: false,
    labelPosition: undefined,
    appearance: undefined,
    hasMore: false,
    loadingMore: false,
    tooltip: undefined,
    helpLink: undefined,
    disableHoverColorTag: false,
    useSmallerVerticalPadding: false,
    itemIconSize: undefined,
    noMobileMargin: false,
    truncateClosedTag: false,
  },
);

const emit = defineEmits<{
  "update:modelValue": [value: SelectableItem["id"]];
  "update:searchTerm": [searchTerm: string];
  opened: [];
  closed: [];
  loadMore: [];
}>();
const modelProxy = useVModel(props, "modelValue", emit, { passive: true });
const searchTermProxy = useVModel(props, "searchTerm", emit, { passive: true });
const selectedItem = computed(() => props.items.find((i) => i.id === modelProxy.value));
</script>

<style module lang="scss">
@use "../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;

.root {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  gap: var(--space-component-gap-small);
  &.validationError {
    color: var(--color-content-negative-secondary);
  }
}

.label {
  @include FigmaTypoMixins.typo-body-medium;
}
</style>
